import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { formatDateForDisplay, formatDateForInput, isMaskedDob, MASKED_DOB_DISPLAY } from "../../../utils/formatters";
import {
  getDateConstraintError,
  getHtmlDateMinMax,
} from "../../../utils/dateFieldConstraints";
import {
  TAX_BRACKET_MAP,
  getAccountRiskTimeHorizonLabel,
  parseAccountRiskTimeHorizonDisplay,
  ACCOUNT_TIME_HORIZON_MAP,
} from "../../../constants/householdConstants";
import {
  formatRoutingNumberInput,
  validateRoutingNumber,
  formatSSNInput,
  validateSSNDigits,
} from "../../../utils/validationHelpers";


const formatPhoneInput = (raw) => {
  const d = (raw || "").replace(/\D/g, "").substring(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const formatCurrency = (value) => {
  if (!value) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatRange = (range) => {
  if (!range || (range.start === null && range.end === null)) return "N/A";
  const start = range.start !== null ? formatCurrency(range.start) : "N/A";
  const end = range.end !== null ? formatCurrency(range.end) : "N/A";
  return `${start} - ${end}`;
};

const formatAccountType = (accountType) => {
  if (accountType == null || accountType === "") return "N/A";
  const s = String(accountType).trim();
  if (!s) return "N/A";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, " ");
};

const formatTaxBracket = (taxInfo) => {
  if (
    !taxInfo ||
    !taxInfo.tax_bracket ||
    (taxInfo.tax_bracket.start === null && taxInfo.tax_bracket.end === null)
  ) {
    return "N/A";
  }
  const entry = Object.entries(TAX_BRACKET_MAP).find(
    ([, v]) => v.start === taxInfo.tax_bracket.start && v.end === taxInfo.tax_bracket.end
  );
  return entry ? entry[0] : "N/A";
};

const formatInvestmentTimeHorizon = (timeHorizon) => {
  if (!timeHorizon || typeof timeHorizon !== "object") return timeHorizon || "";
  return getAccountRiskTimeHorizonLabel(timeHorizon, "");
};

const parseInvestmentTimeHorizon = (displayValue) =>
  parseAccountRiskTimeHorizonDisplay(displayValue);

const DataField = React.memo(
  ({
    label,
    value,
    isEditable = false,
    type = "text",
    min = null,
    max = null,
    isEditing = false,
    options = null,
    optionLabels = null,
    allowCustom = false,
    isRange = false,
    rangeData = null,
    dateValue = null,
    fieldPath = null,
    onFieldChange = null,
    getFormValue = null,
    disableWheel = false,
    commitOnChange = false,
    error = null,
    onFieldError = null,
    dateConstraint = null,
    maxLength = null,
    ssnLengthOnly = false,
  }) => {
    const resolveValue = useCallback(
      (path, fallback) => {
        if (!getFormValue || !path) return fallback;
        return getFormValue(path, fallback);
      },
      [getFormValue],
    );

    const resolvedValue = resolveValue(fieldPath, value);
    const resolvedDateValue = resolveValue(fieldPath, dateValue);
    const resolvedRangeStart = resolveValue(`${fieldPath}.start`, rangeData?.start);
    const resolvedRangeEnd = resolveValue(`${fieldPath}.end`, rangeData?.end);

    const htmlDateBounds = useMemo(
      () => getHtmlDateMinMax(dateConstraint),
      [dateConstraint],
    );

    const [localValue, setLocalValue] = useState("");
    const [localRangeStart, setLocalRangeStart] = useState("");
    const [localRangeEnd, setLocalRangeEnd] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const hasLocalChange = useRef(false);
    const dateInputRef = useRef(null);
    const dropdownRef = useRef(null);

    const filteredOptions = useMemo(() => {
      if (!options) return [];
      const q = allowCustom ? searchQuery : "";
      if (!q) return options;
      return options.filter((opt) =>
        String(opt).toLowerCase().includes(q.toLowerCase()),
      );
    }, [options, searchQuery, allowCustom]);

    const deriveValue = useCallback(() => {
      if (type === "number") {
        return resolvedValue !== undefined && resolvedValue !== null && resolvedValue !== "N/A"
          ? resolvedValue.toString().replace(/[^0-9.-]/g, "")
          : "";
      }
      if (type === "date") {
        if (resolvedDateValue && isMaskedDob(String(resolvedDateValue))) {
          return MASKED_DOB_DISPLAY;
        }
        return resolvedDateValue ? formatDateForInput(resolvedDateValue) : "";
      }
      if (type === "ssn") {
        const raw = resolvedValue !== undefined && resolvedValue !== null ? String(resolvedValue) : "";
        return formatSSNInput(raw);
      }
      if (type === "phone") {
        const raw = resolvedValue !== undefined && resolvedValue !== null ? String(resolvedValue) : "";
        return formatPhoneInput(raw);
      }
      if (type === "routing") {
        const raw = resolvedValue !== undefined && resolvedValue !== null ? String(resolvedValue) : "";
        return formatRoutingNumberInput(raw);
      }
      return resolvedValue !== undefined && resolvedValue !== null && resolvedValue !== "N/A"
        ? resolvedValue
        : "";
    }, [resolvedValue, resolvedDateValue, type]);

    useEffect(() => {
      if (!isEditing) {
        hasLocalChange.current = false;
        return;
      }
      if (!hasLocalChange.current) {
        setLocalValue(deriveValue());
        setLocalRangeStart(
          resolvedRangeStart !== undefined && resolvedRangeStart !== null ? resolvedRangeStart : "",
        );
        setLocalRangeEnd(
          resolvedRangeEnd !== undefined && resolvedRangeEnd !== null ? resolvedRangeEnd : "",
        );
      }
    }, [deriveValue, resolvedRangeStart, resolvedRangeEnd, isEditing]);

    useEffect(() => {
      if (!dropdownOpen) {
        setSearchQuery("");
        return;
      }
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownOpen]);

    const handleChange = (e) => {
      let val = e.target.value;
      if (type === "number") {
        if (val.length > 1 && val.startsWith("0") && !val.startsWith("0.")) {
          val = val.replace(/^0+(?=\d)/, "");
        }
      }
      hasLocalChange.current = true;
      setLocalValue(val);
      if (commitOnChange && onFieldChange && fieldPath) {
        onFieldChange(fieldPath, val);
      }
    };

    const handleRangeStartChange = (e) => {
      let val = e.target.value;
      if (val.length > 1 && val.startsWith("0") && !val.startsWith("0.")) {
        val = val.replace(/^0+(?=\d)/, "");
      }
      hasLocalChange.current = true;
      setLocalRangeStart(val);
    };

    const handleRangeEndChange = (e) => {
      let val = e.target.value;
      if (val.length > 1 && val.startsWith("0") && !val.startsWith("0.")) {
        val = val.replace(/^0+(?=\d)/, "");
      }
      hasLocalChange.current = true;
      setLocalRangeEnd(val);
    };

    const handleBlur = (path, valueToPersist, fieldType = null) => () => {
      let sanitizedValue = valueToPersist;
      if (type === "ssn" && sanitizedValue === "" && String(resolvedValue).includes('*')) {
        sanitizedValue = formatSSNInput(String(resolvedValue));
        setLocalValue(sanitizedValue);
      }
      if (type === "number" && sanitizedValue !== "" && sanitizedValue !== null) {
        let numValue = parseFloat(sanitizedValue);
        if (numValue < 0) {
          numValue = 0;
          if (onFieldChange && path) onFieldChange(path, "0");
          if (isRange) {
            if (fieldType === "start") setLocalRangeStart("0");
            else setLocalRangeEnd("0");
          } else {
            setLocalValue("0");
          }
          if (onFieldError) onFieldError(path, null);
          return;
        }
        if (max !== null && numValue > max) {
          const isYears = path && path.includes("investor_experience");
          const errorMessage = isYears
            ? `Years of experience cannot exceed ${max}.`
            : `${label} cannot exceed ${max}.`;
          if (onFieldError) onFieldError(path, errorMessage);
          return;
        }
        if (onFieldError) onFieldError(path, null);
      }

      if (isRange && path) {
        const startVal = fieldType === "start" ? valueToPersist : localRangeStart;
        const endVal = fieldType === "end" ? valueToPersist : localRangeEnd;
        if (startVal !== "" && endVal !== "" && startVal !== null && endVal !== null) {
          if (parseFloat(startVal) > parseFloat(endVal)) {
            if (onFieldError) onFieldError(fieldPath, "Invalid Range");
            return;
          }
        }
        if (onFieldError) onFieldError(fieldPath, null);
      }

      if (onFieldChange && path) {
        onFieldChange(path, sanitizedValue);
      }
    };

    return (
      <div className="py-2">
        <span className="text-sm font-medium text-gray-500">{label}</span>
        {isEditing && isEditable ? (
          isRange ? (
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min={0}
                placeholder="From"
                value={localRangeStart}
                onChange={handleRangeStartChange}
                onBlur={handleBlur(`${fieldPath}.start`, localRangeStart, "start")}
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                onWheel={disableWheel ? (e) => e.currentTarget.blur() : undefined}
                className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  error ? "border-red-500" : "border-gray-300"
                }`}
              />
              <span className="text-gray-500">to</span>
              <input
                type="number"
                min={0}
                placeholder="To"
                value={localRangeEnd}
                onChange={handleRangeEndChange}
                onBlur={handleBlur(`${fieldPath}.end`, localRangeEnd, "end")}
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                onWheel={disableWheel ? (e) => e.currentTarget.blur() : undefined}
                className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  error ? "border-red-500" : "border-gray-300"
                }`}
              />
            </div>
          ) : options ? (
            <div ref={dropdownRef} className="relative mt-1">
              {allowCustom ? (
                <>
                  <div className="relative">
                    <input
                      type="text"
                      value={localValue}
                      onChange={(e) => {
                        hasLocalChange.current = true;
                        setLocalValue(e.target.value);
                        setSearchQuery(e.target.value);
                        if (!dropdownOpen) setDropdownOpen(true);
                      }}
                      onFocus={() => {
                        setSearchQuery(localValue || "");
                        setDropdownOpen(true);
                      }}
                      onBlur={() => {
                        if (onFieldChange && fieldPath) onFieldChange(fieldPath, localValue);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.currentTarget.blur();
                          setDropdownOpen(false);
                        }
                      }}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      className={`w-full px-3 py-2 pr-8 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 ${
                        error ? "border-red-500 bg-red-50/30" : "border-gray-300"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery("");
                        setDropdownOpen((o) => !o);
                      }}
                      className="absolute inset-y-0 right-0 flex items-center pr-2"
                      tabIndex={-1}
                    >
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${dropdownOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  {dropdownOpen && filteredOptions.length > 0 && (
                    <ul className="absolute top-full left-0 z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredOptions.map((option, index) => (
                        <li
                          key={index}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            hasLocalChange.current = true;
                            setLocalValue(option);
                            setDropdownOpen(false);
                            if (onFieldChange && fieldPath) onFieldChange(fieldPath, option);
                          }}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${
                            localValue === option ? "bg-emerald-50 text-emerald-700 font-medium" : "text-gray-900"
                          }`}
                        >
                          {optionLabels ? optionLabels[options.indexOf(option)] : option}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <>
              <button
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm text-left flex items-center justify-between bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 ${
                  error ? "border-red-500 bg-red-50/30" : "border-gray-300"
                }`}
              >
                <span className={localValue ? "text-gray-900" : "text-gray-400"}>
                  {localValue
                    ? optionLabels
                      ? (optionLabels[options.indexOf(localValue)] ?? localValue)
                      : localValue
                    : `Select ${label}`}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${dropdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {dropdownOpen && (
                <ul className="absolute top-full left-0 z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                  <li
                    onClick={() => {
                      hasLocalChange.current = true;
                      setLocalValue("");
                      setDropdownOpen(false);
                      if (onFieldChange && fieldPath) onFieldChange(fieldPath, "");
                    }}
                    className="px-3 py-2 text-sm text-gray-400 cursor-pointer hover:bg-gray-50"
                  >
                    Select {label}
                  </li>
                  {options.map((option, index) => (
                    <li
                      key={index}
                      onClick={() => {
                        hasLocalChange.current = true;
                        setLocalValue(option);
                        setDropdownOpen(false);
                        if (onFieldChange && fieldPath) onFieldChange(fieldPath, option);
                      }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${
                        localValue === option ? "bg-emerald-50 text-emerald-700 font-medium" : "text-gray-900"
                      }`}
                    >
                      {optionLabels ? optionLabels[options.indexOf(option)] : option}
                    </li>
                  ))}
                </ul>
              )}
                </>
              )}
            </div>
          ) : type === "date" ? (
            <div
              className="relative cursor-pointer mt-1"
              onClick={() => {
                if (dateInputRef.current?.showPicker) dateInputRef.current.showPicker();
                else dateInputRef.current?.focus();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  dateInputRef.current?.showPicker?.();
                }
              }}
            >
              <input
                type="text"
                readOnly
                value={localValue ? formatDateForDisplay(localValue) : ""}
                placeholder={`Select ${label.toLowerCase()}`}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white cursor-pointer ${
                  error ? "border-red-500" : "border-gray-300"
                }`}
                aria-readonly
              />
              <input
                ref={dateInputRef}
                type="date"
                value={localValue ? formatDateForInput(localValue) : ""}
                min={htmlDateBounds.min}
                max={htmlDateBounds.max}
                onChange={(e) => {
                  const raw = e.target.value;
                  const val = raw ? formatDateForInput(raw) : "";
                  hasLocalChange.current = true;
                  setLocalValue(val);
                  if (onFieldChange && fieldPath) onFieldChange(fieldPath, val);
                  if (onFieldError && fieldPath && dateConstraint) {
                    onFieldError(
                      fieldPath,
                      getDateConstraintError(val, dateConstraint, label),
                    );
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                style={{ cursor: "pointer" }}
                tabIndex={-1}
                aria-hidden
              />
            </div>
          ) : type === "ssn" ? (
            <input
              type="text"
              value={localValue}
              placeholder="XXX-XX-XXXX"
              maxLength={11}
              onFocus={(e) => {
                if (String(localValue).includes("*")) {
                  setLocalValue("");
                }
              }}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^\d*]/g, "").substring(0, 9);
                const formatted = formatSSNInput(digits);
                hasLocalChange.current = true;
                setLocalValue(formatted);
                if (onFieldChange && fieldPath) onFieldChange(fieldPath, formatted);
                if (digits.length === 9) {
                  if (!ssnLengthOnly && !validateSSNDigits(digits)) {
                    if (onFieldError) onFieldError(fieldPath, "Invalid SSN/TIN");
                  } else {
                    if (onFieldError) onFieldError(fieldPath, null);
                  }
                } else {
                  if (onFieldError) onFieldError(fieldPath, null);
                }
              }}
              onBlur={() => {
                let digits = localValue.replace(/[^\d*]/g, "");
                if (digits.length === 0 && String(resolvedValue).includes('*')) {
                  digits = String(resolvedValue).replace(/[^\d*]/g, "");
                  const formatted = formatSSNInput(digits);
                  setLocalValue(formatted);
                  if (onFieldChange && fieldPath) onFieldChange(fieldPath, formatted);
                }
                if (digits.length === 0) {
                  if (onFieldError) onFieldError(fieldPath, null);
                } else if (digits.length < 9) {
                  if (onFieldError) onFieldError(fieldPath, "Incomplete SSN/TIN");
                } else if (!ssnLengthOnly && !validateSSNDigits(digits)) {
                  if (onFieldError) onFieldError(fieldPath, "Invalid SSN/TIN");
                } else {
                  if (onFieldError) onFieldError(fieldPath, null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm tracking-widest ${
                error ? "border-red-500 bg-red-50/30" : "border-gray-300"
              }`}
            />
          ) : type === "phone" ? (
            <input
              type="text"
              value={localValue}
              placeholder="(XXX) XXX-XXXX"
              maxLength={14}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").substring(0, 10);
                const formatted = formatPhoneInput(digits);
                hasLocalChange.current = true;
                setLocalValue(formatted);
                if (onFieldChange && fieldPath) onFieldChange(fieldPath, formatted);
                if (onFieldError) onFieldError(fieldPath, null);
              }}
              onBlur={() => {
                const digits = localValue.replace(/\D/g, "");
                if (digits.length === 0) {
                  if (onFieldError) onFieldError(fieldPath, null);
                } else if (digits.length < 10) {
                  if (onFieldError) onFieldError(fieldPath, "Phone number must be 10 digits");
                } else {
                  if (onFieldError) onFieldError(fieldPath, null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                error ? "border-red-500 bg-red-50/30" : "border-gray-300"
              }`}
            />
          ) : type === "routing" ? (
            <input
              type="text"
              inputMode="numeric"
              value={localValue}
              placeholder="Enter routing number (9 digits)"
              maxLength={9}
              onChange={(e) => {
                const formatted = formatRoutingNumberInput(e.target.value);
                hasLocalChange.current = true;
                setLocalValue(formatted);
                if (onFieldChange && fieldPath) onFieldChange(fieldPath, formatted);
                if (onFieldError) onFieldError(fieldPath, null);
              }}
              onBlur={() => {
                if (!localValue) {
                  if (onFieldError) onFieldError(fieldPath, null);
                } else if (!validateRoutingNumber(localValue)) {
                  if (onFieldError)
                    onFieldError(fieldPath, "Please enter a valid 9-digit routing number");
                } else {
                  if (onFieldError) onFieldError(fieldPath, null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                error ? "border-red-500 bg-red-50/30" : "border-gray-300"
              }`}
            />
          ) : (
            <input
              type={type}
              value={localValue}
              min={min}
              max={max}
              maxLength={maxLength || undefined}
              onChange={handleChange}
              onBlur={handleBlur(fieldPath, localValue)}
              onKeyDown={(e) => {
                if (type === "number") {
                  if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                  if (e.key === "Enter") e.currentTarget.blur();
                }
              }}
              onWheel={disableWheel ? (e) => e.currentTarget.blur() : undefined}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                type === "number"
                  ? "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  : ""
              } ${error ? "border-red-500" : "border-gray-300"}`}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
          )
        ) : (
          <p className="text-sm text-gray-900 mt-1">
            {type === "ssn"
              ? (value ? formatSSNInput(String(value)) : "N/A")
              : type === "phone"
                ? (value ? formatPhoneInput(String(value)) : "N/A")
                : value || "N/A"}
          </p>
        )}
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    );
  },
);

DataField.displayName = "DataField";

const ToggleField = ({
  label,
  value,
  isEditable = false,
  isEditing = false,
  fieldPath = null,
  onFieldChange = null,
  formData = null,
  /** "row" = label left / control right (full width). "field" = stacked like DataField (for grids). */
  variant = "row",
}) => {
  const getCurrentValue = () => {
    if (!fieldPath || !formData) return value;
    const keys = fieldPath.split(".");
    let current = formData;
    for (const key of keys) {
      if (current && current[key] !== undefined) {
        current = current[key];
      } else {
        return value;
      }
    }
    return current !== undefined ? current : value;
  };

  const currentValue = getCurrentValue();

  const handleToggle = () => {
    const newValue = !currentValue;
    if (onFieldChange && fieldPath) {
      onFieldChange(fieldPath, newValue);
    }
  };

  const control =
    isEditing && isEditable ? (
      <button
        type="button"
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
          currentValue ? "bg-emerald-600" : "bg-gray-200"
        }`}
        onClick={handleToggle}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            currentValue ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    ) : (
      <div
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          value ? "bg-emerald-600" : "bg-gray-200"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            value ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </div>
    );

  if (variant === "field") {
    return (
      <div className="py-2">
        <span className="text-sm font-medium text-gray-500">{label}</span>
        <div className="mt-1">{control}</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {control}
    </div>
  );
};

const CollapsibleSection = ({ title, isExpanded, onToggle, children, icon, subtitle }) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-gray-900">{title}</span>
          {subtitle && <span className="text-sm font-medium">{subtitle}</span>}
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && <div className="p-4 bg-white">{children}</div>}
    </div>
  );
};

const OwnerDropdownField = ({
  label,
  value,
  isEditable = false,
  isEditing = false,
  disabled = false,
  excludeValue = null,
  householdMembers = [],
  fieldPath = null,
  onFieldChange = null,
  getFormValue = null,
}) => {
  const getCurrentValue = () => {
    if (!fieldPath || !getFormValue) {
      if (!value) return "";
      const memberFromLabel = householdMembers.find((m) => {
        const memberName = `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim();
        return memberName === value;
      });
      return memberFromLabel ? String(memberFromLabel.id) : "";
    }
    const stored = getFormValue(fieldPath, null);
    if (stored === null || stored === undefined || stored === "") {
      if (!value) return "";
      const memberFromLabel = householdMembers.find((m) => {
        const memberName = `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim();
        return memberName === value;
      });
      return memberFromLabel ? String(memberFromLabel.id) : "";
    }
    return String(stored);
  };

  const currentValue = getCurrentValue();

  const handleChange = (e) => {
    if (onFieldChange && fieldPath) {
      const selectedId = e.target.value;
      onFieldChange(fieldPath, selectedId || null);
    }
  };

  return (
    <div className="py-2">
      <span className="text-sm font-medium text-gray-500">{label}</span>
      {isEditing && isEditable ? (
        <select
          value={disabled ? "" : currentValue}
          onChange={handleChange}
          disabled={disabled}
          className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
            disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          <option value="">Select {label}</option>
          {householdMembers
            .map((m) => {
              const memberName = `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim();
              if (memberName && memberName !== excludeValue) {
                return (
                  <option key={m.id} value={m.id}>
                    {memberName}
                  </option>
                );
              }
              return null;
            })
            .filter(Boolean)}
        </select>
      ) : (
        <p className="text-sm text-gray-900 mt-1">
          {value || (disabled ? "N/A (single-owner account)" : "N/A")}
        </p>
      )}
    </div>
  );
};

export {
  DataField,
  ToggleField,
  CollapsibleSection,
  OwnerDropdownField,
  formatSSNInput,
  formatPhoneInput,
  validateSSNDigits,
  formatCurrency,
  formatRange,
  formatAccountType,
  formatTaxBracket,
  ACCOUNT_TIME_HORIZON_MAP as TIME_HORIZON_MAP,
  formatInvestmentTimeHorizon,
  parseInvestmentTimeHorizon,
};
